import React, { useState, useEffect } from 'react';
import { Bell, Check, X, AlertCircle, Info, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNotificacionesContext } from '../../context/NotificacionesContext';
import notificacionesAPI from '../../services/notificacionesAPI';

export default function NotificacionesDropdown({ isOpen, onClose }) {
  const { user } = useAuth();
  const { 
    stats, 
    marcarComoLeida: marcarLeidaContexto, 
    eliminarNotificacion: eliminarContexto, 
    marcarTodasLeidas: marcarTodasLeidasContexto 
  } = useNotificacionesContext();
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  // Cargar notificaciones reales del backend
  useEffect(() => {
    if (isOpen && user) {
      cargarNotificaciones();
    }
  }, [isOpen, user]);

  const cargarNotificaciones = async () => {
    try {
      setLoading(true);
      const response = await notificacionesAPI.obtenerNotificaciones({
        limit: 20
      });
      
      setNotificaciones(response.notificaciones || []);
    } catch (error) {
      console.error('Error cargando notificaciones:', error);
      // Fallback a notificaciones de ejemplo en caso de error
      setNotificaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const getIconoTipo = (tipo) => {
    switch (tipo) {
      case 'success':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'warning':
        return <AlertCircle size={16} className="text-yellow-500" />;
      case 'error':
        return <X size={16} className="text-red-500" />;
      default:
        return <Info size={16} className="text-emerald-500" />;
    }
  };

  const formatearFecha = (fechaStr) => {
    const fecha = new Date(fechaStr);
    const ahora = new Date();
    const diferencia = ahora - fecha;
    const minutos = Math.floor(diferencia / 60000);
    const horas = Math.floor(diferencia / 3600000);
    const dias = Math.floor(diferencia / 86400000);

    if (minutos < 60) {
      return `hace ${minutos} min`;
    } else if (horas < 24) {
      return `hace ${horas}h`;
    } else {
      return `hace ${dias}d`;
    }
  };

  const marcarComoLeida = async (id) => {
    try {
      const success = await marcarLeidaContexto(id);
      if (success) {
        setNotificaciones(prev => 
          prev.map(notif => 
            notif._id === id ? { ...notif, leida: true } : notif
          )
        );
      }
    } catch (error) {
      console.error('Error marcando notificación como leída:', error);
    }
  };

  const eliminarNotificacion = async (id) => {
    try {
      const notifEliminada = notificaciones.find(n => n._id === id);
      const success = await eliminarContexto(id, !notifEliminada?.leida);
      if (success) {
        setNotificaciones(prev => prev.filter(notif => notif._id !== id));
      }
    } catch (error) {
      console.error('Error eliminando notificación:', error);
    }
  };

  const marcarTodasLeidas = async () => {
    try {
      const success = await marcarTodasLeidasContexto();
      if (success) {
        setNotificaciones(prev => 
          prev.map(notif => ({ ...notif, leida: true }))
        );
      }
    } catch (error) {
      console.error('Error marcando todas como leídas:', error);
    }
  };

  const notificacionesNoLeidas = stats.no_leidas;

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 mt-3 w-[22rem] overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 shadow-[0_24px_60px_-26px_rgba(15,23,42,0.5)] backdrop-blur-xl z-50">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100/90 bg-gradient-to-r from-emerald-50/60 via-white to-cyan-50/40 p-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Notificaciones</h3>
          <p className="mt-0.5 text-[11px] text-slate-500">Alertas recientes del sistema</p>
        </div>
        <div className="flex items-center gap-2">
          {notificacionesNoLeidas > 0 && (
            <>
              <span className="rounded-full bg-red-500 px-2 py-1 text-xs font-semibold text-white shadow-sm">
                {notificacionesNoLeidas}
              </span>
              <button
                onClick={marcarTodasLeidas}
                className="rounded-lg bg-emerald-100/80 p-1 text-emerald-700 transition-colors hover:bg-emerald-200/80 hover:text-emerald-800"
                title="Marcar todas como leídas"
              >
                <Check size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Contenido */}
      <div className="max-h-96 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {loading ? (
          <div className="p-5 text-center text-slate-500">
            <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            Cargando notificaciones...
          </div>
        ) : notificaciones.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <Bell size={24} className="opacity-80" />
            </div>
            <p className="text-sm font-medium text-slate-600">No tienes notificaciones</p>
            <p className="mt-1 text-xs text-slate-400">Cuando haya novedades aparecerán aquí.</p>
          </div>
        ) : (
          <div className="py-1.5">
            {notificaciones.map((notif) => (
              <div
                key={notif._id}
                className={`border-b border-slate-100/80 px-4 py-3.5 transition-colors hover:bg-slate-50/80 ${
                  !notif.leida ? 'bg-emerald-50/50' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-slate-100">
                    {getIconoTipo(notif.tipo)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h4 className={`text-sm ${!notif.leida ? 'font-semibold' : 'font-medium'} text-slate-900`}>
                        {notif.titulo}
                      </h4>
                      <span className="ml-2 text-[11px] text-slate-500">
                        {formatearFecha(notif.fecha_creacion)}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{notif.mensaje}</p>
                    
                    {/* Acciones */}
                    <div className="mt-2.5 flex items-center gap-2">
                      {notif.link && (
                        <a
                          href={notif.link}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-100/70 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
                        >
                          Ver detalle
                        </a>
                      )}
                      {!notif.leida && !notif.es_virtual && (
                        <button
                          onClick={() => marcarComoLeida(notif._id)}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-emerald-700 hover:bg-slate-200"
                        >
                          <Check size={12} />
                          Marcar como leída
                        </button>
                      )}
                      {!notif.es_virtual && (
                        <button
                          onClick={() => eliminarNotificacion(notif._id)}
                          className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs text-red-600 hover:bg-red-100 hover:text-red-700"
                        >
                          <X size={12} />
                          Eliminar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notificaciones.length > 0 && (
        <div className="border-t border-slate-100/90 bg-slate-50/80 p-3">
          <button className="w-full rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 hover:text-emerald-800">
            Ver todas las notificaciones
          </button>
        </div>
      )}
    </div>
  );
}